/**
 * 점검 관리 타입 정의
 */

/** 점검 DTO */
export interface MaintenanceDto {
    seqNo: number;
    startDate: string;
    endDate: string;
    msgCode: string;
    msgContent: string | null;
    useYn: string;
    insUser: string | null;
    insDate: string | null;
    updUser: string | null;
    updDate: string | null;
}

/** 점검 목록 응답 DTO */
export interface GetMaintenanceListResponseDto {
    maintenances: MaintenanceDto[];
    totalCount: number;
}

/** 현재 활성화된 점검 응답 DTO */
export interface GetActiveMaintenanceResponseDto {
    isMaintenance: boolean;
    seqNo: number | null;
    startDate: string | null;
    endDate: string | null;
    msgCode: string | null;
    msgContent: string | null;
}

/** 점검 등록 요청 DTO */
export interface CreateMaintenanceRequestDto {
    startDate: string;
    endDate: string;
    msgCode: string;
    msgContent?: string;
    useYn?: string;
    insUser?: string;
}

/** 점검 수정 요청 DTO */
export interface UpdateMaintenanceRequestDto {
    seqNo: number;
    startDate?: string;
    endDate?: string;
    msgCode?: string;
    msgContent?: string;
    useYn?: string;
    updUser?: string;
}

/** 점검 처리 응답 DTO */
export interface MaintenanceResponseDto {
    seqNo: number;
    success: boolean;
    message: string;
}

/** 도메인 모델 - 점검 */
export interface Maintenance {
    seqNo: number;
    startDate: Date;
    endDate: Date;
    msgCode: string;
    msgContent: string | null;
    useYn: string;
    insUser: string | null;
    insDate: Date | null;
    updUser: string | null;
    updDate: Date | null;
}

/**
 * 날짜 파싱 헬퍼 함수
 * LocalDateTime 배열 형식 또는 ISO 문자열 형식 모두 처리
 */
const parseDate = (value: string | number[] | null | undefined): Date | null => {
    if (!value) return null;

    // 배열 형식: [2026, 1, 6, 13, 0, 0] (LocalDateTime 직렬화)
    if (Array.isArray(value)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = value;
        return new Date(year, month - 1, day, hour, minute, second);
    }

    // 문자열 형식: ISO 또는 다른 형식
    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    return null;
};

/** DTO → Domain 변환 */
export const maintenanceFromDto = (dto: MaintenanceDto): Maintenance => ({
    seqNo: dto.seqNo,
    startDate: parseDate(dto.startDate as unknown as string | number[]) || new Date(),
    endDate: parseDate(dto.endDate as unknown as string | number[]) || new Date(),
    msgCode: dto.msgCode,
    msgContent: dto.msgContent,
    useYn: dto.useYn,
    insUser: dto.insUser,
    insDate: parseDate(dto.insDate as unknown as string | number[]),
    updUser: dto.updUser,
    updDate: parseDate(dto.updDate as unknown as string | number[]),
});

/** 점검 목록 DTO → Domain 변환 */
export const maintenanceListFromDto = (dto: GetMaintenanceListResponseDto): Maintenance[] =>
    dto.maintenances.map(maintenanceFromDto);
